// What a COMPLETE migration looks like, declared once.
//
// The step ledger only holds steps that were actually registered, so nothing in
// it can say a step is missing: a step introduced after a member migrated reads
// as absent from the ledger and as satisfied by every consumer. This manifest is
// the other half of that question — the expected set — and it lives here rather
// than in each runner because three separate consumers need it and only this
// package is below all three:
//
//   • the Tier 0 / Tier 1 / sona-context / legacy-mirror / analytics-history
//     runners in `apps/api`,
//     whose step vocabularies are PROJECTIONS of this tuple rather than copies;
//   • the operator CLIs in `scripts/db-tooling`, which cannot import `apps/**`
//     at all — the reason their step lists were hand-copied and drifted;
//   • the gap report, which answers "expected minus recorded" per member.
//
// TWO INDEPENDENT AXES. `owner` names the runner that REGISTERS the step (it is
// what decides which consumer a DLQ replay must re-invoke). `mirror` marks the
// steps whose work the ADR-138 legacy-mirror module performs, which is a
// different question: `tier0.prescriptions` is owned by the Tier 0 transfer and
// performed by the mirror. Keeping them apart is what lets every drift guard be
// a projection instead of a second list.
//
// ORDER. Entries are in run order per owner, which is the order the Tier 0 and
// Tier 1 reports rank by. The `mirror: true` projection is therefore NOT in the
// order `LEGACY_MIRROR_STEP_NAMES` historically used — the two orders are
// mutually exclusive, because Tier 1 runs telemedicine appointments before
// intentions and that list named them the other way round. The mirror set's
// order carries no meaning (its consumers are a literal-union type and a
// register lookup), so this file's order wins and the mirror list is compared as
// a set.

import { MEMBER_MIGRATION_STEP_NAME_PATTERN, type MemberMigrationTier } from './schema';

/** Which runner registers a step — the unit a DLQ replay re-invokes. */
export const MIGRATION_STEP_OWNERS = [
  'migration-start',
  'tier0-transfer',
  'tier1-transfer',
  'sona-context',
  'legacy-mirror',
  'analytics-history',
  'chat-import',
] as const;
export type MigrationStepOwner = (typeof MIGRATION_STEP_OWNERS)[number];

/**
 * Whether `member.migration.replay_requested` can heal an owner's steps.
 *
 * A replay is the one canonical restore path, so every owner has to say which
 * side of it they are on. `healable: true` is a promise that the owner's
 * consumer subscribes to replay requests; a contract test in `apps/api` reads
 * the CDK rules and holds every such owner to it. `healable: false` needs a
 * reason a reader can check, because it is the statement that a gap in that
 * owner's steps is repaired some other way.
 */
export type MigrationStepOwnerReplay = { healable: true } | { healable: false; reason: string };

/**
 * Each owner's replay coverage. A `Record` over the owner tuple, so adding an
 * owner fails the typecheck here until it is declared one way or the other.
 */
export const MIGRATION_STEP_OWNER_REPLAY: Readonly<
  Record<MigrationStepOwner, MigrationStepOwnerReplay>
> = {
  'migration-start': {
    healable: false,
    reason:
      'Tier 0 runs synchronously inside /migration/start, which re-runs a member whose marker is not done; every replay consumer refuses a member whose marker is not done.',
  },
  'tier0-transfer': {
    healable: false,
    reason:
      'Tier 0 runs synchronously inside /migration/start, which re-runs a member whose marker is not done; every replay consumer refuses a member whose marker is not done.',
  },
  'tier1-transfer': { healable: true },
  'sona-context': { healable: true },
  'legacy-mirror': { healable: true },
  'analytics-history': {
    healable: false,
    reason:
      'Its rule carries member.migrated only, since a replay request would re-read the whole legacy history. A dead-lettered delivery is repaired by redriving its own DLQ (migration:dlq-replay --consumer analytics-history); a step that never ran or was skipped for a retryable reason is re-run by migration:step-backfill --step tier2.analytics_history, and a settled or human-class one by the same command with --operator-manual --member.',
  },
  'chat-import': { healable: true },
};

export interface MigrationStepManifestEntry {
  /** The ledger's `step_name`. Always prefixed with its own tier. */
  readonly step: string;
  readonly tier: MemberMigrationTier;
  readonly owner: MigrationStepOwner;
  /** True when the ADR-138 legacy-mirror module performs this step's work. */
  readonly mirror: boolean;
}

/**
 * Every step a migrated member is expected to carry, in run order per owner.
 *
 * Adding a step here without adding it to its runner — or the reverse — fails
 * the drift guard in `apps/api/src/migration/migration-step-manifest.contract.test.ts`,
 * which is the only place that can see all three vocabularies at once.
 */
export const MIGRATION_STEP_MANIFEST = [
  // The orchestrator's own phases: each can fail for a migration-level reason,
  // so each carries its own row rather than being folded into the transfer.
  { step: 'tier0.freeze', tier: 'tier0', owner: 'migration-start', mirror: false },
  { step: 'tier0.source_read', tier: 'tier0', owner: 'migration-start', mirror: false },
  { step: 'tier0.transfer', tier: 'tier0', owner: 'migration-start', mirror: false },

  // The synchronous import the member waits on.
  { step: 'tier0.timezone_seed', tier: 'tier0', owner: 'tier0-transfer', mirror: false },
  { step: 'tier0.profile', tier: 'tier0', owner: 'tier0-transfer', mirror: false },
  // The home address, written inside the transfer so the AI availability read
  // that follows the import sees it. Tier 1's `tier1.addresses` re-runs the same
  // idempotent append and finds the rows already present.
  { step: 'tier0.addresses', tier: 'tier0', owner: 'tier0-transfer', mirror: false },
  // The member's state from their 1.0 sponsor profile, for a member whose 1.0
  // address is too partial to admit; an address, once imported, wins at read.
  { step: 'tier0.jurisdiction', tier: 'tier0', owner: 'tier0-transfer', mirror: false },
  { step: 'tier0.program', tier: 'tier0', owner: 'tier0-transfer', mirror: false },
  { step: 'tier0.check_ins', tier: 'tier0', owner: 'tier0-transfer', mirror: false },
  { step: 'tier0.rewards', tier: 'tier0', owner: 'tier0-transfer', mirror: false },
  { step: 'tier0.care_specialist', tier: 'tier0', owner: 'tier0-transfer', mirror: false },
  { step: 'tier0.quit_dates', tier: 'tier0', owner: 'tier0-transfer', mirror: false },
  { step: 'tier0.prescriptions', tier: 'tier0', owner: 'tier0-transfer', mirror: true },
  { step: 'tier0.substance_cards', tier: 'tier0', owner: 'tier0-transfer', mirror: true },

  // The asynchronous set the Sona readiness gate waits for.
  // The native baselines run first, straight after the shared 1.0 read. They
  // depend on nothing else in this tier (only Tier 0's programme step, which
  // they check through the ledger); running them first lands the designated
  // baseline seconds after `member.migrated`, ahead of every other Tier 1
  // domain, so the re-onboarding call's live baseline read finds it.
  { step: 'tier1.baselines', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  { step: 'tier1.appointments', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  // The catch-up for past 1.0 appointments `tier1.appointments` used to drop
  // because their practitioner is no Galapagos specialist. A member migrated
  // before it existed shows it missing, so a replay request heals them; a
  // member migrated after finds every row already written.
  { step: 'tier1.unmatched_appointments', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  {
    step: 'tier1.telemedicine_appointments',
    tier: 'tier1',
    owner: 'tier1-transfer',
    mirror: true,
  },
  { step: 'tier1.healthie_link', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  { step: 'tier1.intentions', tier: 'tier1', owner: 'tier1-transfer', mirror: true },
  { step: 'tier1.addresses', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  // The member's 1.0 nicotine-replacement order history: every shipment, its
  // status history and the request behind it. It sits after the addresses
  // because every order points at an address that step wrote.
  { step: 'tier1.nrt_orders', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  // The member's 1.0 breathalyzer and urine drug test cup shipments, as order
  // history under each one's own programme. Shares the shipment tables with the
  // NRT step but none of its rules: nothing it writes is a pending decision.
  { step: 'tier1.test_kit_orders', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  // The member's 1.0 breathalyzer readings, under their alcohol episode. Reads
  // its own table and depends on no earlier step's writes.
  { step: 'tier1.breathalyzer_readings', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  // The member's 1.0 order history. Tier 0 debits the points; this lands the
  // orders themselves, so a migrated member's history is never empty.
  { step: 'tier1.cm_orders', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  { step: 'tier1.gender_identity', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  { step: 'tier1.parental_consent', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  { step: 'tier1.recording_consent', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  // The member's 1.0 health-plan identifiers (plan member ID and group number)
  // from the current b2b row, filled onto the profile only while both columns
  // are empty. Outside the readiness gate: see MIGRATION_READINESS_EXEMPT_STEPS.
  { step: 'tier1.plan_details', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  // The member's 1.0 eligible verdict as a matched eligibility resolution, for
  // members whose Tier 0 ran before Tier 0 wrote it. A member whose Tier 0 did
  // write it finds the row and writes nothing.
  { step: 'tier1.imported_resolution', tier: 'tier1', owner: 'tier1-transfer', mirror: false },
  // The 1.0 baseline headers and their per-substance day rows as delivered. The
  // native baselines are built from the same read, not from these mirrors, so
  // they need not wait for them.
  { step: 'tier1.baseline_mirror', tier: 'tier1', owner: 'tier1-transfer', mirror: true },
  { step: 'tier1.baseline_alcohol', tier: 'tier1', owner: 'tier1-transfer', mirror: true },
  { step: 'tier1.baseline_cannabis', tier: 'tier1', owner: 'tier1-transfer', mirror: true },
  { step: 'tier1.baseline_opioid', tier: 'tier1', owner: 'tier1-transfer', mirror: true },
  { step: 'tier1.baseline_stimulant', tier: 'tier1', owner: 'tier1-transfer', mirror: true },
  // The latest 1.0 tobacco/vape profile at migration (an ADR-138 projection
  // mirror). Depends on no other step, so it can run last.
  { step: 'tier1.tobacco_profile', tier: 'tier1', owner: 'tier1-transfer', mirror: true },
  { step: 'tier1.sona_context', tier: 'tier1', owner: 'sona-context', mirror: false },

  // Best-effort backfill. Every legacy-mirror tier 2 step is a
  // destination-absent 1.0 table imported as a typed mirror by the Tier 2
  // runner, plus product-table imports (`tier2.diagnoses`,
  // `tier2.change_healthcare_ids`). The chat history import at the end has its
  // own consumer because it writes into the member's live care-chat thread
  // rather than a mirror table.
  { step: 'tier2.diagnoses', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.primary_diagnosis', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.secondary_diagnosis', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.consultations', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.consultations_follow_up', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.typical_opioid_use', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.typical_substance_use', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.fagerstrom_scores', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  {
    step: 'tier2.fagerstrom_chewing_scores',
    tier: 'tier2',
    owner: 'legacy-mirror',
    mirror: true,
  },
  { step: 'tier2.urine_results', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.emergency_contact', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.emergency_surgery', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.oud_consultations', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.history_of_substance_use', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.medical_assessment', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.medical_information', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.medication_experience', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.opioids_status', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.oud_member_history', tier: 'tier2', owner: 'legacy-mirror', mirror: true },

  // Also best-effort, and Tier 2 for the same reason: nothing about the member's
  // readiness depends on it. The replay of the member's legacy product-analytics
  // history is its own `member.migrated` consumer, not a Tier 2 runner step, so
  // it is the one tier 2 entry the Tier 2 runner does not dispatch. Registering
  // it under tier 1 would let a slow, failing or dead-lettered analytics copy
  // hold `tier1Complete` false and the member at the handoff.
  {
    step: 'tier2.analytics_history',
    tier: 'tier2',
    owner: 'analytics-history',
    mirror: false,
  },
  { step: 'tier2.staff_notes', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.next_contact_due_dates', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.coach_assignments', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.coach_interactions', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.treatment_activity', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.fields_values', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.gogomeds_orders', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.gogomeds_order_items', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.health_warehouse_orders', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.health_warehouse_users', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.apero_coverages', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.provider_history', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.personal_information', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  // Writes the native `change_healthcare_ids` table rather than a mirror, like
  // `tier2.diagnoses`; its ledger row is also the per-member "import ran" marker.
  { step: 'tier2.change_healthcare_ids', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  // 1.0 `user_pathways` → `legacy_user_pathways` (ADR-138 mirror).
  { step: 'tier2.clinical_pathways', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  // The member's 1.0 Essentials module progress: completions from 1.0 stage
  // rows, dated on the member's local day, and the mapped step rows. Tier 2,
  // so a member is handed over without waiting on it: until it lands, a module
  // finished in 1.0 reads as not yet complete. Writes product tables, not a
  // mirror, but the legacy-mirror module performs it, like the diagnoses import.
  { step: 'tier2.module_progress', tier: 'tier2', owner: 'legacy-mirror', mirror: true },
  { step: 'tier2.chat_history', tier: 'tier2', owner: 'chat-import', mirror: false },
] as const satisfies readonly MigrationStepManifestEntry[];

type ManifestEntry = (typeof MIGRATION_STEP_MANIFEST)[number];

export type MigrationStepName = ManifestEntry['step'];
/** The step names one runner registers, as a literal union. */
export type MigrationStepOf<O extends MigrationStepOwner> = Extract<
  ManifestEntry,
  { owner: O }
>['step'];
/** The step names of one tier, as a literal union. */
export type MigrationStepOfTier<T extends MemberMigrationTier> = Extract<
  ManifestEntry,
  { tier: T }
>['step'];
/** The step names the legacy-mirror module performs, as a literal union. */
export type MigrationMirrorStepName = Extract<ManifestEntry, { mirror: true }>['step'];

/**
 * Whether a runner dispatches a tier's steps today.
 *
 * This is the difference between "the member is missing eleven steps" and "no
 * code has run these yet". A gap report over an undispatched tier would name
 * every step for every migrated member, which is noise, not a finding — so the
 * report states `dispatched: false` and reports nothing missing instead.
 *
 * A tier flips to `true` when a runner registers its steps. Every tier now has
 * one, so a member migrated before the Tier 2 runner existed reports every Tier 2
 * step missing until a replay or the sweep backfills it.
 *
 * Reporting only: no runner, event rule or consumer reads this to decide whether
 * a tier runs.
 */
export const MIGRATION_TIER_DISPATCH: Readonly<Record<MemberMigrationTier, boolean>> = {
  tier0: true,
  tier1: true,
  tier2: true,
};

/**
 * Tier 1 steps the readiness gate does not wait for, each with the reason.
 *
 * `tier1Complete` on the migration status read holds a member at the pre-Sona
 * handoff until every manifest Tier 1 step is recorded and complete. A step whose
 * data the handoff never reads belongs here, so that a member migrated before
 * the step existed, or one whose step is still settling, is not held for it.
 * The step still runs, still records its ledger row, and still shows in the gap
 * report; only the gate ignores it.
 */
export const MIGRATION_READINESS_EXEMPT_STEPS: Readonly<
  Partial<Record<MigrationStepOfTier<'tier1'>, string>>
> = {
  'tier1.plan_details':
    'Insurance identifiers are read by eligibility operations and billing, never by the handoff or the Sona conversation; members migrated before the step existed reach it through replay.',
  'tier1.unmatched_appointments':
    'Past appointment history is read by Atlas and the previous-calls list, never by the handoff or the Sona conversation, which read only upcoming bookings; members migrated before the step existed reach it through replay.',
};

/** Whether the readiness gate waits for this step. */
export function isReadinessExemptStep(step: string): boolean {
  return Object.hasOwn(MIGRATION_READINESS_EXEMPT_STEPS, step);
}

// The projections every consumer derives its own vocabulary from.
//
// Each narrows through a type predicate with a CONCRETE owner, never a generic
// one: with a concrete `Extract` the compiler resolves the narrowed element type
// and the mapped array carries the same literal union the hand-written tuple
// did, so a consumer's `…StepName` type survives the move with no assertion
// anywhere. A generic helper cannot do that — its `Extract` stays deferred and
// the result widens to `string`, which would silently unnarrow every consumer.

function ownedBy<O extends MigrationStepOwner>(
  owner: O,
): (e: ManifestEntry) => e is Extract<ManifestEntry, { owner: O }> {
  return (e): e is Extract<ManifestEntry, { owner: O }> => e.owner === owner;
}

/** The orchestrator's own Tier 0 phases, in the order it performs them. */
export const MANIFEST_STEPS_MIGRATION_START: readonly MigrationStepOf<'migration-start'>[] =
  MIGRATION_STEP_MANIFEST.filter(ownedBy('migration-start')).map((e) => e.step);

/** The Tier 0 transfer's steps, in run order. */
export const MANIFEST_STEPS_TIER0_TRANSFER: readonly MigrationStepOf<'tier0-transfer'>[] =
  MIGRATION_STEP_MANIFEST.filter(ownedBy('tier0-transfer')).map((e) => e.step);

/** The Tier 1 transfer's steps, in run order. */
export const MANIFEST_STEPS_TIER1_TRANSFER: readonly MigrationStepOf<'tier1-transfer'>[] =
  MIGRATION_STEP_MANIFEST.filter(ownedBy('tier1-transfer')).map((e) => e.step);

/** The sona-context consumer's step. */
export const MANIFEST_STEPS_SONA_CONTEXT: readonly MigrationStepOf<'sona-context'>[] =
  MIGRATION_STEP_MANIFEST.filter(ownedBy('sona-context')).map((e) => e.step);

/** The analytics-history replay consumer's step. */
export const MANIFEST_STEPS_ANALYTICS_HISTORY: readonly MigrationStepOf<'analytics-history'>[] =
  MIGRATION_STEP_MANIFEST.filter(ownedBy('analytics-history')).map((e) => e.step);
/** The chat history import consumer's step. */
export const MANIFEST_STEPS_CHAT_IMPORT: readonly MigrationStepOf<'chat-import'>[] =
  MIGRATION_STEP_MANIFEST.filter(ownedBy('chat-import')).map((e) => e.step);

/**
 * Every step the legacy-mirror module performs, whichever runner owns it.
 *
 * Its order is this file's, NOT the order the hand-written list used — see the
 * ORDER note at the top. Consumers treat it as a set.
 */
export const MANIFEST_STEPS_MIRROR: readonly MigrationMirrorStepName[] =
  MIGRATION_STEP_MANIFEST.filter(
    (e): e is Extract<ManifestEntry, { mirror: true }> => e.mirror,
  ).map((e) => e.step);

/**
 * The manifest's steps for one tier, in run order.
 *
 * Returns plain strings: the gap report compares them against ledger rows whose
 * `step_name` is `text`, so a literal union would buy nothing and would force
 * the caller to carry the tier as a type parameter.
 */
export function manifestStepsForTier(tier: MemberMigrationTier): readonly string[] {
  return MIGRATION_STEP_MANIFEST.filter((e) => e.tier === tier).map((e) => e.step);
}

/** The manifest's steps registered by one runner, in that runner's run order. */
export function manifestStepsForOwner(owner: MigrationStepOwner): readonly string[] {
  return MIGRATION_STEP_MANIFEST.filter((e) => e.owner === owner).map((e) => e.step);
}

/** Every mirror step, as plain strings, for a set comparison. */
export function manifestMirrorSteps(): readonly string[] {
  return [...MANIFEST_STEPS_MIRROR];
}

/** Every manifest step matches the ledger's own `step_name` bound. */
export function manifestStepNamesAreWellFormed(): boolean {
  return MIGRATION_STEP_MANIFEST.every(
    (e) => MEMBER_MIGRATION_STEP_NAME_PATTERN.test(e.step) && e.step.startsWith(`${e.tier}.`),
  );
}

// ─── what a settled step's reason means for reconciliation ───────────────────
//
// A `skipped` step is not a gap on its own: most skips are a step correctly
// finding nothing to do. The report has to separate the three cases, and the
// separation is a property of the reason code, so it is declared once here
// rather than re-derived by each reader.
//
//   settled   — nothing to do, and nothing will change that. Not a gap.
//   retryable — a re-run could now succeed (a dependency has since settled, an
//               infrastructure fault has passed). Replay 2/3 acts on these.
//   human     — a person must look at the 1.0 data before anything can move.
//               `human_reconciliation` is the canonical one, and it is
//               deliberately NOT retryable: retrying it forever is what hides
//               the fact that somebody has to make a decision.
//
// A code with no class classifies `human`. That direction is chosen on purpose:
// an unrecognised reason is more likely to need a person than a retry loop, and
// the exhaustiveness guard means an unclassified code is caught in CI anyway.

export const MIGRATION_STEP_REASON_CLASSES = ['settled', 'retryable', 'human'] as const;
export type MigrationStepReasonClass = (typeof MIGRATION_STEP_REASON_CLASSES)[number];

/**
 * The only reasons an operator may settle a human-class `failed` step with.
 *
 *   operator_accepted_loss      — the 1.0 rows are gone on purpose (a deletion
 *                                 request, a deliberate archive) and the empty
 *                                 result is correct.
 *   operator_data_unrecoverable — the 1.0 data a step refuses cannot be fixed
 *                                 at source, and a person has accepted that this
 *                                 member goes without it.
 *
 * Both classify `settled`, so the gap report and the default replay stop
 * treating the step as outstanding. A closed list rather than free text: the
 * reason is what a reader filters on, and the operator's own words go in the
 * note beside it.
 */
export const OPERATOR_SETTLE_REASONS = [
  'operator_accepted_loss',
  'operator_data_unrecoverable',
] as const;
export type OperatorSettleReason = (typeof OPERATOR_SETTLE_REASONS)[number];

export function isOperatorSettleReason(value: string): value is OperatorSettleReason {
  return OPERATOR_SETTLE_REASONS.some((reason) => reason === value);
}

export const MIGRATION_STEP_REASON_CLASS: Readonly<Record<string, MigrationStepReasonClass>> = {
  // Nothing to do, and nothing to retry.
  source_absent: 'settled',
  no_substances: 'settled',
  no_ledger_rows: 'settled',
  no_assignment_rows: 'settled',
  no_tobacco_episode: 'settled',
  specialist_absent: 'settled',
  destination_absent: 'settled',
  client_link_absent: 'settled',
  prescriber_only: 'settled',
  // The member has no legacy product-analytics history to replay.
  no_history: 'settled',
  // Analytics-history replay: legacy rows were read, and the transform
  // dropped every one of them (identity events, unscrubbable or oversize rows).
  all_dropped: 'settled',
  // The 1.0 history sits under an account archived for a deletion request.
  deletion_request: 'settled',
  // An operator confirmed a human-class failure is a legitimate, final
  // outcome and settled it with `migration:settle-step`. See
  // `OPERATOR_SETTLE_REASONS` for what each one asserts.
  operator_accepted_loss: 'settled',
  operator_data_unrecoverable: 'settled',

  // A later run may now succeed: what blocked this one was another step or the
  // infrastructure, not the source data.
  dependency_skipped: 'retryable',
  dependency_failed: 'retryable',
  infrastructure_error: 'retryable',
  legacy_source_unavailable: 'retryable',
  source_unavailable: 'retryable',
  // A table the step needs was not in the read (reader older than registration,
  // or Tier 0/1 consent import before user_consents is reachable). Re-run retryable.
  source_unread: 'retryable',
  // Analytics-history replay: the environment had no legacy source or no
  // destination sink when the step ran. A re-run once both are configured
  // replays the member, which is exactly what the step backfill is for.
  source_unconfigured: 'retryable',
  sink_unconfigured: 'retryable',
  // Ran before the member's Tier 0 marker had committed.
  marker_missing: 'retryable',
  // The legacy read, the destination write or the ledger write itself failed.
  source_read_failed: 'retryable',
  sink_write_failed: 'retryable',
  // Sona-context's three step failures. Each wraps work whose member-dependent
  // causes are all transient: the reads re-gather the document from current
  // state on every attempt; the write is one conflict-tolerant insert of a
  // bounded document under a member the ledger's own foreign key has already
  // proved; and `step_failed` — the recorder's own statements and the
  // dependency composition — is metric-only today, never written to a ledger
  // row. What is left deterministic in any of them is a code or deploy defect
  // that fails every delivery alike, and that is surfaced by the consumer's DLQ
  // alarm and failure metric, not by a per-member class. Revisit `step_failed`
  // if anything ever records it on a ledger row.
  read_failed: 'retryable',
  write_failed: 'retryable',
  step_failed: 'retryable',
  // Chat history import: the member has no primary coach to provision the
  // thread with yet, or the 1.0 chat seam was not configured or not reachable.
  no_primary_coach: 'retryable',
  chat_source_unavailable: 'retryable',
  // `source_read_failed` (above) is shared with the chat import.
  // The chat import holds attachments but the deployment has no copier, or
  // every attachment read was refused: wiring or a grant, not the data.
  attachments_unavailable: 'retryable',
  attachment_unreadable: 'retryable',

  // Somebody has to look at 1.0 before this can move.
  human_reconciliation: 'human',
  // Analytics-history replay: the producer sent no lookup id the legacy
  // project could be asked about, or the source returned a page the keyset
  // cursor could not move past. The same input fails the same way on retry.
  lookup_id_invalid: 'human',
  source_unpageable: 'human',
  bad_source_row: 'human',
  // A Tier 2 step whose transaction failed for a reason the same rows will
  // raise again — a constraint violation, a value its column refuses, a schema
  // mismatch, a statement or request over the Data API's limits, a mapping
  // defect. Retrying it changes nothing, so it waits for a person.
  deterministic_error: 'human',
  foreign_member_row: 'human',
  // A step that reads another step's writes refused to run because that step
  // failed human-class or was settled by an operator. A retry finds the same
  // upstream, so the dependent waits on the same person. See
  // `dependency-refusal.ts`; `dependency_failed` above is the retryable case.
  dependency_unrecoverable: 'human',
  healthie_id_taken: 'human',
  // One chat-history row is larger than a single Data API request can carry;
  // the import is refused whole rather than retried into the same refusal.
  message_too_large: 'human',
  // The chart is missing or archived in Healthie, so no retry of ours changes
  // it: someone reconciles in Healthie, or sets the link once it is active.
  healthie_client_inactive: 'human',
  // Declared in the Tier 1 vocabulary but not currently emitted — a differing
  // email still links and is recorded on the step counts. Classified anyway so
  // the class is a decision rather than the unknown-code fallback, and so the
  // day it is emitted it already lands where a mismatch belongs: with a person
  // deciding which of the two records is right.
  email_mismatch: 'human',
  legacy_source_rejected: 'human',
  invalid_zone: 'human',
  unknown_substance: 'human',
  unknown_operation_type: 'human',
  unknown_subtype: 'human',
  negative_reward_row: 'human',
  positive_redemption_row: 'human',
  source_ref_owned_by_other_member: 'human',
  role_unresolved: 'human',
  assignment_conflict: 'human',
  client_link_not_granted: 'human',
  // Analytics-history codes for a delivery that names no usable member. Not
  // written to a step today (there is no member to key one on); classified so
  // the class is a decision if that changes.
  malformed_event: 'human',
  missing_member: 'human',
  // Chat history import: no trustworthy cutover time to filter 1.0 messages
  // by, or the member's thread cannot be provisioned as it stands.
  cutover_unknown: 'human',
  member_ineligible: 'human',
  thread_conflict: 'human',
  // An earlier attempt of the step read 1.0 rows and this one read none: the
  // rows were archived or deleted on the 1.0 side after the member migrated.
  // A retry reads the same nothing, so a person decides what the loss means.
  source_rows_vanished: 'human',
};

/**
 * The bare code of a stored reason or warning, with any `:<qualifier>` removed.
 *
 * A stored reason or warning MAY be qualified with the coordinate or value the
 * condition arose at — `unknown_substance:<value>`, `human_reconciliation:<table>.<kind>`,
 * `value_fallback:<table>.<row>.<column>` — so the qualifier carries 1.0
 * identifiers: substance values, table names, row ids, even a 1.0 UUID. The
 * ledger column keeps the whole string, because that is the audit record and
 * the recorder's pattern bounds its shape. Anything that *renders* a reason or
 * warning outside the database takes the code alone, through this function, so
 * one definition of "the bare code" serves both the classification (where a
 * qualified reason must land in the same bucket as its unqualified form) and
 * every renderer (where the qualifier must not reach an operator's terminal or
 * a machine-readable document).
 */
export function stepDetailCode(value: string): string {
  return value.split(':', 1)[0] ?? '';
}

/**
 * Classify what a settled step recorded in `error`.
 *
 * The bare code is taken first — a qualified reason must classify identically
 * to its unqualified form, or the same condition would land in two buckets
 * depending on whether the writer happened to add detail.
 */
export function classifyStepReason(error: string | null | undefined): MigrationStepReasonClass {
  if (error === null || error === undefined || error === '') return 'human';
  const code = stepDetailCode(error);
  // `Object.hasOwn`, not a bare index: a plain object literal resolves inherited
  // keys, so a reason of `constructor` or `toString` would return an
  // Object.prototype member instead of undefined, the `?? 'human'` fallback would
  // never run, and the caller's class lookup would be `undefined`. No writer
  // produces such a code, but the "any unknown code classifies human" guarantee
  // should hold for any input, not only the inputs we expect.
  if (!Object.hasOwn(MIGRATION_STEP_REASON_CLASS, code)) return 'human';
  return MIGRATION_STEP_REASON_CLASS[code] ?? 'human';
}
